import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, LockIcon, ZapIcon, CreditCardIcon, ArrowRightIcon } from 'lucide-react';
type PackId = 'profesional' | 'elite';
const packs: Record<PackId, {
  id: PackId;
  emoji: string;
  title: string;
  tagline: string;
  priceOld: string;
  priceNew: string;
  cta: string;
  checkoutUrl: string;
  microcopy?: string;
  badges?: {
    text: string;
    variant: 'yellow' | 'orange';
  }[];
}> = {
  profesional: {
    id: 'profesional',
    emoji: '📘',
    title: 'PACK PROFESIONAL',
    tagline: '12 Tomos + 6 Bonus',
    priceOld: '$29.990',
    priceNew: '$18.990',
    cta: 'SÍ, QUIERO EL PACK PROFESIONAL',
    checkoutUrl: 'https://profelisto.com.ar/checkouts/cn/hWNCuqdQLhOILqALwop6Ev4M/es-ar?_r=AQABqNZjIgeoKtCs09rCVUGudTqyOUXRSA0DXoQU_d1XYQQ'
  },
  elite: {
    id: 'elite',
    emoji: '🏆',
    title: 'MEGAPACK ELITE',
    tagline: '12 Tomos + 6 Bonus + 2 EBOOKS ELITE',
    priceOld: '$49.990',
    priceNew: '$27.990',
    cta: 'SÍ, QUIERO EL MEGAPACK ELITE',
    checkoutUrl: 'https://profelisto.com.ar/checkouts/cn/hWNCuqdgNTLuKpHY9l2ahaV8/es-ar?_r=AQABpK9K2AtYg_NJo8T-ws9m2pVEDPReemptQNaUTiBhm9g',
    microcopy: 'La opción más elegida por profesores de tenis argentinos',
    badges: [{
      text: '⭐ MÁS ELEGIDO',
      variant: 'yellow'
    }, {
      text: 'AHORRÁS $10.000',
      variant: 'orange'
    }]
  }
};
export function Pricing() {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 37
  });
  const [selected, setSelected] = useState<PackId>('elite');
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const activePack = packs[selected];
  return <section id="precio" className="py-16 md:py-24 bg-tennis-dark text-white relative scroll-mt-10" data-id="element-200">
      <div className="max-w-2xl mx-auto px-4 relative z-10" data-id="element-201">
        {/* Countdown header */}
        <div className="text-center mb-10" data-id="element-202">
          <h3 className="text-2xl md:text-3xl font-display text-ball-yellow mb-6 leading-tight" data-id="element-203">
            ⏰ PRECIO DE LANZAMIENTO — OFERTA POR TIEMPO LIMITADO
          </h3>

          <div className="inline-flex flex-col items-center bg-black/40 p-5 rounded-2xl border border-white/10 backdrop-blur-sm" data-id="element-204">
            <div className="flex items-center gap-2 md:gap-3 text-3xl md:text-4xl font-mono font-bold tracking-wider mb-2" data-id="element-205">
              <div className="bg-white/10 px-3 py-1.5 rounded-lg" data-id="element-206">
                {String(timeLeft.hours).padStart(2, '0')}
              </div>
              <span className="text-clay-orange" data-id="element-207">:</span>
              <div className="bg-white/10 px-3 py-1.5 rounded-lg" data-id="element-208">
                {String(timeLeft.minutes).padStart(2, '0')}
              </div>
              <span className="text-clay-orange" data-id="element-209">:</span>
              <div className="bg-white/10 px-3 py-1.5 rounded-lg" data-id="element-210">
                {String(timeLeft.seconds).padStart(2, '0')}
              </div>
            </div>
            <div className="flex items-center gap-2 text-red-400 font-medium text-xs md:text-sm" data-id="element-211">
              <span className="animate-pulse" data-id="element-212">🔴</span> El precio sube cuando
              termina el contador
            </div>
          </div>
        </div>

        {/* Checkout card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white text-charcoal rounded-3xl shadow-2xl p-6 md:p-8" data-id="element-213">
          <div className="text-center mb-6" data-id="element-214">
            <h3 className="text-3xl md:text-4xl font-display text-charcoal mb-2" data-id="element-215">
              Elegí tu Pack:
            </h3>
            <p className="text-gray-500 text-sm md:text-base" data-id="element-216">
              Ambas opciones incluyen los 6 Bonus estratégicos
            </p>
          </div>

          {/* Option rows */}
          <div className="space-y-4 mb-8" data-id="element-217">
            {(Object.values(packs) as (typeof packs)[PackId][]).map(pack => {
            const isSelected = selected === pack.id;
            return <button key={pack.id} type="button" onClick={() => setSelected(pack.id)} className={`w-full text-left rounded-2xl border-2 p-4 md:p-5 transition-all flex items-start gap-4 ${isSelected ? 'border-clay-orange bg-clay-orange/5' : 'border-gray-200 bg-white hover:border-gray-300'}`} aria-pressed={isSelected} data-id="element-218">
                  {/* Radio */}
                  <div className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'border-clay-orange' : 'border-gray-300'}`} data-id="element-219">
                    {isSelected && <div className="w-3 h-3 rounded-full bg-clay-orange" data-id="element-220"></div>}
                  </div>

                  {/* Content */}
                  <div className="flex-1 flex flex-col sm:flex-row items-start gap-3 min-w-0" data-id="element-221">
                    <div className="text-3xl leading-none shrink-0" data-id="element-222">
                      {pack.emoji}
                    </div>
                    <div className="flex-1 min-w-0 w-full" data-id="element-223">
                      <div className="flex items-start justify-between gap-3 mb-2" data-id="element-224">
                        <div className="font-display text-base md:text-xl text-charcoal leading-tight flex-1 min-w-0 break-words" data-id="element-225">
                          {pack.title}
                        </div>
                        {/* Price (top-right) */}
                        <div className="text-right shrink-0" data-id="element-226">
                          <div className="text-gray-400 line-through text-xs md:text-sm font-medium" data-id="element-227">
                            {pack.priceOld}
                          </div>
                          <div className={`font-display text-xl md:text-2xl leading-tight ${isSelected ? 'text-clay-orange' : 'text-charcoal'}`} data-id="element-228">
                            {pack.priceNew}
                          </div>
                        </div>
                      </div>

                      {pack.badges && <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-229">
                          {pack.badges.map(b => <span key={b.text} className={`text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-full ${b.variant === 'yellow' ? 'bg-ball-yellow text-tennis-dark' : 'bg-clay-orange/15 text-clay-dark'}`} data-id="element-230">
                              {b.text}
                            </span>)}
                        </div>}

                      <div className="text-xs md:text-sm text-gray-500 leading-snug" data-id="element-231">
                        {pack.tagline}
                      </div>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Dynamic CTA */}
          <a href={activePack.checkoutUrl} target="_blank" rel="noopener noreferrer" className="w-full bg-clay-orange hover:bg-clay-dark text-white font-display text-base md:text-lg py-5 px-6 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 flex flex-col items-center justify-center gap-1" data-id="element-232">
            <span className="text-center leading-tight" data-id="element-233">{activePack.cta}</span>
            <ArrowRightIcon size={20} data-id="element-234" />
          </a>

          {activePack.microcopy && <p className="text-center text-xs md:text-sm text-gray-400 italic mt-4 px-2" data-id="element-235">
              {activePack.microcopy}
            </p>}
        </motion.div>

        {/* Trust badges grid */}
        <div className="mt-8 grid grid-cols-2 gap-y-4 gap-x-6 text-sm text-gray-300 font-medium max-w-md mx-auto" data-id="element-236">
          <div className="flex items-center gap-2" data-id="element-237">
            <LockIcon size={18} className="text-gray-400" data-id="element-238" />
            <span data-id="element-239">Pago seguro</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-240">
            <ZapIcon size={18} className="text-ball-yellow" data-id="element-241" />
            <span data-id="element-242">Acceso en 5 min</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-243">
            <ShieldCheckIcon size={18} className="text-tennis-light" data-id="element-244" />
            <span data-id="element-245">Garantía 7 días</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-246">
            <CreditCardIcon size={18} className="text-gray-400" data-id="element-247" />
            <span data-id="element-248">Tarjeta · Transferencia</span>
          </div>
        </div>
      </div>
    </section>;
}